import { CClient } from '@common/utils'
import { SCHOOL_FIELD_INPUT_LIMITS } from '@lib/constants/competitions'
import { SchoolResponseType } from '@lib/types/competitions'
import { getAPIErrorString } from '@lib/utils/common'
import { captureException } from '@sentry/node'
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import Autosuggest, {
  ChangeEvent,
  InputProps,
  SuggestionSelectedEventData,
} from 'react-autosuggest'
import theme from 'styles/react-autosuggest.module.css'
import { useDebouncedCallback } from 'use-debounce'

type Props = {
  setSchoolName: React.Dispatch<React.SetStateAction<string>>
  setSchoolId: React.Dispatch<React.SetStateAction<number>>
  schoolName: string
  errorText: string
  handleOnBlur?: () => void
}

const renderSuggestion = (suggestion: SchoolResponseType) => (
  <div className="px-3 py-2 cursor-pointer text-grey">{suggestion?.title}</div>
)

const SelectSchool = ({
  setSchoolName,
  setSchoolId,
  schoolName,
  errorText,
  handleOnBlur,
}: Props) => {
  const [suggestions, setSuggestions] = useState<SchoolResponseType[]>([])
  const [isFocused, setIsFocused] = useState<boolean>(false)

  const fetchSchoolSuggestions = async (value: string) => {
    const updatedValueLen = value.trim().length
    const { MIN_LENGTH, MAX_LENGTH } = SCHOOL_FIELD_INPUT_LIMITS
    if (updatedValueLen < MIN_LENGTH || updatedValueLen > MAX_LENGTH) return
    try {
      const requestUrl = `/api/v1/schools/?q=${value}`
      const response = await CClient(requestUrl)
      if (!response.ok)
        throw new Error(await getAPIErrorString(response, requestUrl))

      const schoolResults = await response.json()
      setSuggestions(schoolResults)
    } catch (error) {
      captureException(error)
    }
  }

  const [debouncedFetchSuggestions] = useDebouncedCallback(
    ({ value }) => fetchSchoolSuggestions(value),
    300
  )

  const handleOnChange = (
    e: React.FormEvent<HTMLElement>,
    { newValue }: ChangeEvent
  ) => {
    if (!suggestions.length) setSchoolId(null) // reset school ID if no suggestions, meaning it's a new school
    setSchoolName(newValue)
  }

  const handleOnSuggestionSelected = (
    e: React.FormEvent<HTMLElement>,
    { suggestion }: SuggestionSelectedEventData<SchoolResponseType>
  ) => {
    setSchoolId(suggestion.id)
    setSchoolName(suggestion.title)
  }

  const handleOnClear = () => setSuggestions([])

  const getSuggestionValue = (suggestion: SchoolResponseType) =>
    suggestion.title

  const handleOnBackSpace = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.keyCode === 8) setSchoolId(null)
  }

  const inputProps: InputProps<SchoolResponseType> = {
    value: schoolName,
    onChange: handleOnChange,
    name: 'school',
    id: 'school',
    type: 'text',
    required: true,
    onKeyDown: (e) => handleOnBackSpace(e),
    onFocus: () => setIsFocused(true),
    onBlur: () => {
      schoolName === '' && setIsFocused(false)
      handleOnBlur && handleOnBlur()
    },
  }

  useEffect(() => {
    schoolName && setIsFocused(true)
  }, [schoolName])

  return (
    <div className="relative">
      <Autosuggest
        theme={theme}
        suggestions={suggestions}
        onSuggestionsFetchRequested={debouncedFetchSuggestions}
        onSuggestionsClearRequested={handleOnClear}
        renderSuggestion={renderSuggestion}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={handleOnSuggestionSelected}
        inputProps={inputProps}
      />

      <label
        htmlFor="school"
        className={classNames(
          'absolute top-0 left-0 flex cursor-text items-center',
          'p-2 px-4 transition-all duration-200 ease-in-out',
          'mt-1 select-none',
          { 'text-xs text-grey-800 pointer-events-none mt-auto': isFocused }
        )}
      >
        School name
      </label>
      {errorText && (
        <div className="mt-1 text-sm text-red-200 font-400">{errorText}</div>
      )}
    </div>
  )
}

export default SelectSchool
